import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Lightbulb, CheckCircle, ArrowLeft, Download, Video, Star, Target, Users, ExternalLink, Phone, Mail, MessageSquare, Send } from 'lucide-react';
import { StepCard, VideoCard, InfoCard, ExampleCard } from './SectionComponents';

const SalesCaseStudySection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {
  const [checkedItems, setCheckedItems] = useState({});

  const coldCallFramework = [
    {
      step: "1. Opener",
      description: "Be confident, polite and concise",
      example: '"Hi [name], I\'ll keep it quick — is now a bad time?"'
    },
    {
      step: "2. Hook / reason for call",
      description: "Give context — why are you calling them specifically?",
      example: '"I work with [role]s at [company type] to help with [problem]..."'
    },
    {
      step: "3. Discovery / qualifying questions",
      description: "Ask 1-2 short questions to learn more before pitching",
      example: '"How are you currently handling [X]?" Listen and adapt — show curiosity'
    },
    {
      step: "4. Pitch the value (keep it short)",
      description: "Focus on benefits, not features",
      example: '"We help teams like yours reduce [X] by doing [Y] — most of our users see results in [timeframe]."'
    },
    {
      step: "5. Call to action",
      description: "Suggest a follow-up, not a hard sell",
      example: '"Would you be open to booking in 15 mins next week to see if this might be useful?"'
    }
  ];

  const coldCallExamples = [
    {
      name: "Luke, Senior BDE @ Autogen",
      url: "https://www.loom.com/share/ac825f4acad2442e9d95f87ee2d99bf3?sid=045bd6ad-85bd-451e-80ea-79138409d54e",
      creator: "Watch out for the way Luke is able to build rapport & make it personal.",
      title: "Luke's Cold Call Example"
    },
    {
      name: "Charlie, SDR @ Oculo",
      url: "https://www.loom.com/share/df267f534ebd4a22be897e508ecb640e?sid=85f5f717-96bb-46d4-a4c6-72afabd381d0",
      creator: "Watch out for how Charlie is laser focused on the prospects pain points.",
      title: "Charlie's Cold Call Example"
    },
    {
      name: "Sarah, BDR @ Scan.com",
      url: "https://www.loom.com/share/b0d67f9a0aad4f5f8dd28f0d31fe42e6?sid=4e794fab-7912-4c44-8878-ceacf17c0a3d",
      creator: "Watch for how Sarah listens, she takes on the points the prospect makes, doesn't challenge them but shows how the product solves them.",
      title: "Sarah's Cold Call Example"
    }
  ];

  const bonusPrepTips = [
    'Stand up when you practise — it helps your energy come through',
    'Record yourself and listen back — what\'s your tone like? Are you too fast? Too quiet?',
    'Practice objection handling (e.g. "not interested", "we\'re already using someone", "no budget")'
  ];

  const caseStudyAssessment = [
    'Understand a product and its ideal customer',
    'Research and qualify leads',
    'Write compelling cold outreach that gets replies',
    'Show creativity and initiative'
  ];

  const outOfBoxExamples = [
    {
      title: "Loom videos",
      description: "Introducing yourself or pitching a product"
    },
    {
      title: "Scribeless-style handwritten notes", 
      description: "For high-value prospects"
    },
    {
      title: "Jumpstart 'donut' campaign",
      description: "Using donuts as an incentive when outbounding!"
    },
    {
      title: "LinkedIn voicenotes",
      description: "A great way of cutting through the noise on LinkedIn"
    },
    {
      title: "In-person interaction",
      description: "Going to offices, seeking them out at a conference, using events to warm up targets via thought leadership or lead gen content"
    }
  ];

  const finalTips = [
    'Keep copy short, human and specific — avoid long intros or over-explaining',
    'Focus on why this person and why now',
    'Test tone by reading messages out loud — would you say this on a call?'
  ];

  const checklistItemsRoleplay = [
    'Practiced your mock cold call with Gemma or Channon'
  ];

  const checklistItemsCaseStudy = [
    'Read the brief fully — highlight key goals or constraints',
    'Structured your response — intro, analysis, recommendation',
    'Stayed within scope and time limits — quality over length',
    'Stated assumptions — explain if something was unclear',
    'Finished with a summary and even better, "what I\'d explore next"'
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              The task: roleplays and prospecting take-home case study
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Master both live sales simulations and strategic prospecting challenges.
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">

          <StepCard title="📞 Suggested structure for a cold call">
            <div className="space-y-10">
              <p className="text-gray-600 text-lg leading-relaxed">
                Startups often include a <span className="font-semibold text-gray-800">live cold call roleplay or mock sales pitch</span> as part of the interview process.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-4 text-lg">You might be asked to:</h5>
                <div className="space-y-2">
                  {[
                    'Act like you\'re calling a potential prospect',
                    'Walk through a short pitch of the product',
                    'Handle objections, ask discovery questions or book a meeting'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-8 text-xl">Simple framework to follow:</h4>
                <div className="space-y-6">
                  {coldCallFramework.map((step, index) => (
                    <div key={index} className="bg-white rounded-2xl p-6 border border-gray-200">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-[#02B491] rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-900 mb-2 text-lg">{step.step}</h5>
                          <p className="text-gray-600 leading-relaxed mb-3">{step.description}</p>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <span className="text-gray-700 italic">Example: {step.example}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Check out these best practice examples:</h4>
                <div className="grid gap-6 lg:grid-cols-3">
                  {coldCallExamples.map((example, index) => (
                    <ExampleCard
                      key={index}
                      emoji="📞"
                      title={example.title}
                      links={[{
                        url: example.url,
                        text: "Watch Example",
                        icon: Video,
                        className: "bg-gradient-to-r from-[#02B491] to-emerald-600 text-white hover:shadow-lg hover:shadow-emerald-500/20"
                      }]}
                    >
                      <p className="text-gray-600 leading-relaxed text-sm">{example.creator}</p>
                    </ExampleCard>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold text-slate-900 mb-6 text-xl">Some bonus prep tips:</h4>
                <div className="grid gap-4">
                  {bonusPrepTips.map((tip, i) => (
                    <div key={i} className="flex items-start space-x-4 bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                      <Star className="w-5 h-5 text-yellow-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  Remember
                </h5>
                <p className="text-gray-700 leading-relaxed">
                  You can roleplay a cold call or get feedback on your pitch by booking time in with our interview coaches:
                </p>
                <div className="mt-4 flex flex-wrap gap-4">
                  <a 
                    href="https://calendly.com/gemma-jumpstart-uk/sales-interview-training" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Book with Gemma
                  </a>
                  <a 
                    href="https://calendly.com/channon-jumpstart-uk/sales-interview-training" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Book with Channon
                  </a>

                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  Want more practice?
                </h5>
                <p className="text-gray-700 leading-relaxed">
                For additional practice, Hyperbound is a helpful resource - you can get 9 free AI roleplay sessions for free!
                </p>
                <div className="mt-4 flex flex-wrap gap-4">
                  <a 
                    href="https://www.hyperbound.ai/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Practice Now
                  </a>
              
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="📋 How to approach a take-home case study">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Most sales prospecting case studies are designed to assess how you'd:
              </p>
              
              <div className="grid gap-4 md:grid-cols-2">
                {caseStudyAssessment.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                    <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                    <span className="text-gray-800 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </StepCard>

          <StepCard title="Best-in-class examples & templates">
            <div className="space-y-12">
              <p className="text-gray-600 text-lg leading-relaxed">
                This example shows you what 'great' looks like:
              </p>
              
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl p-8 border border-gray-200">
                    <h5 className="font-bold text-gray-900 mb-3 text-xl">Autogen AI: commercial targeting</h5>
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">A comprehensive approach to identifying and reaching target customers</p>
                    
                    <div className="flex flex-wrap gap-4 mb-6">
                      <a 
                        href="https://docs.google.com/presentation/d/1wxK2gSw7RFqB8ZQOYF6HVTX3wmyHqPj1/edit?usp=sharing&ouid=105118644053723523964&rtpof=true&sd=true" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download Slides
                        <ExternalLink className="w-4 h-4 ml-2 opacity-75" />
                      </a>
                    </div>
                  </div>
                  
                  <div>
                    <h6 className="font-semibold text-gray-900 mb-4 text-lg">Watch the walkthrough:</h6>
                    <VideoCard 
                      title="Autogen AI: commercial targeting - Walkthrough"
                      url="https://www.loom.com/share/ad0cf917943c4ed59c94285fa893b7a0"
                      description="Detailed explanation of the approach and key insights"
                    />
                  </div>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="💌 Cold email & LinkedIn templates">
            <div className="space-y-12">
              <p className="text-gray-600 text-lg leading-relaxed">
                Use these as starting points — adapt them based on the product, audience and use case.
              </p>
              
              <div className="grid gap-8 md:grid-cols-2">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#02B491]" />
                    Cold email template
                  </h5>
                  <div className="bg-gray-50 rounded-xl p-4 text-sm">
                    <div className="space-y-3">
                      <div><span className="font-semibold">Subject:</span> Quick idea for [company name]</div>
                      <div>Hey [name] — I came across [company] and thought I'd reach out.</div>
                      <div>I help [role]s at [industry/company type] tackle [problem] by [value proposition].</div>
                      <div>If [pain or result] is something you're thinking about right now, happy to share a quick idea or resource.</div>
                      <div>Best,<br />[Your Name]</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <h5 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#02B491]" />
                    LinkedIn template
                  </h5>
                  <div className="bg-gray-50 rounded-xl p-4 text-sm">
                    <div className="space-y-3">
                      <div><span className="font-semibold">Hey [name]</span> — noticed [personal/company detail] and had an idea around [problem] we solve.</div>
                      <div>Happy to share something that's worked with other [role]s if you're up for it. Let me know!</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StepCard>

          <StepCard title="💡 Thinking outside the box">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Inboxes are generally clogged up with cold emails — <span className="font-semibold text-gray-800">the best outreach stands out from the crowd</span>.
              </p>
              
              <div>
                <h5 className="font-bold text-gray-900 mb-6 text-lg">Examples that have worked:</h5>
                <div className="grid gap-4 md:grid-cols-2">
                  {outOfBoxExamples.map((example, i) => (
                    <div key={i} className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg hover:shadow-gray-900/5 transition-all duration-300">
                      <h6 className="font-semibold text-gray-900 mb-2">{example.title}</h6>
                      <p className="text-gray-600 text-sm">{example.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="font-bold text-gray-900 mb-6 text-lg">Some final tips:</h5>
                <div className="grid gap-4">
                  {finalTips.map((tip, i) => (
                    <div key={i} className="flex items-start space-x-4 bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <Lightbulb className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg">Want feedback on your outreach copy or prospecting case study?</h5>
                <p className="text-gray-700 leading-relaxed">
                  Send a draft to{' '}
                  <a href="mailto:tom@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 font-medium">
                    tom@jumpstart-uk.com
                  </a>
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="✅ Cold call roleplay and/or case study round checklist">
            <div className="space-y-8">
              <div>
                <h5 className="font-bold text-gray-900 mb-4 text-lg">For a cold call roleplay, make sure you've:</h5>
                <div className="grid gap-3">
                  {checklistItemsRoleplay.map((item, i) => (
                    <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h5 className="font-bold text-gray-900 mb-4 text-lg">For a case study task, before sending your draft, make sure you've:</h5>
                <div className="grid gap-3">
                  {checklistItemsCaseStudy.map((item, i) => (
                    <div key={i} className="flex items-start space-x-3 bg-white rounded-xl p-4 border border-gray-200">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-semibold">Optional:</span> Use slides or visuals if it helps you communicate better.
                </p>
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesCaseStudySection; 